import { supabase } from '@/lib/supabase'
import type {
  Party,
  PartyWithDetails,
  PartyWithNationWorld,
  CreatePartyInput,
  UpdatePartyBasicsInput,
  UpdatePartyResourcesInput,
  IdeologyVector,
  CanCreatePartyResult,
} from '@/types'

// Query key factory for consistent cache management
export const partyKeys = {
  all: ['parties'] as const,
  byNation: (nationId: string) => ['parties', 'nation', nationId] as const,
  detail: (id: string) => ['parties', 'id', id] as const,
  detailWithData: (id: string) => ['parties', 'detail', id] as const,
  myParties: () => ['parties', 'my'] as const,
  myPartiesWithDetails: () => ['parties', 'my', 'withDetails'] as const,
  myPartyInNation: (nationId: string) => ['parties', 'my', 'nation', nationId] as const,
  canCreate: (nationId: string) => ['parties', 'canCreate', nationId] as const,
}

export async function fetchPartiesByNation(nationId: string): Promise<Party[]> {
  const { data, error } = await supabase
    .from('parties')
    .select('*')
    .eq('nation_id', nationId)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  return data as Party[]
}

export async function fetchParty(id: string): Promise<Party> {
  const { data, error } = await supabase.from('parties').select('*').eq('id', id).single()

  if (error) {
    throw error
  }

  return data as Party
}

export async function fetchMyPartyInNation(nationId: string): Promise<Party | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    return null
  }

  const { data, error } = await supabase
    .from('parties')
    .select('*')
    .eq('nation_id', nationId)
    .eq('user_id', user.id)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data as Party | null
}

export async function fetchMyParties(): Promise<Party[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    return []
  }

  const { data, error } = await supabase
    .from('parties')
    .select('*')
    .eq('user_id', user.id)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  return data as Party[]
}

export async function createParty(input: CreatePartyInput): Promise<Party> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Must be logged in to create a party')
  }

  const { data, error } = await supabase
    .from('parties')
    .insert({
      ...input,
      user_id: user.id,
    })
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Party
}

export async function updatePartyBasics(
  id: string,
  updates: UpdatePartyBasicsInput
): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Party
}

export async function updatePartyResources(
  id: string,
  updates: UpdatePartyResourcesInput
): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Party
}

export async function deactivateParty(id: string): Promise<void> {
  const { error } = await supabase.from('parties').update({ is_active: false }).eq('id', id)

  if (error) {
    throw error
  }
}

export async function reactivateParty(id: string): Promise<void> {
  const { error } = await supabase.from('parties').update({ is_active: true }).eq('id', id)

  if (error) {
    throw error
  }
}

export async function fetchPartyWithDetails(id: string): Promise<PartyWithDetails> {
  const { data, error } = await supabase
    .from('parties')
    .select(
      `
      *,
      nation:nations(id, name, world_id, next_election_tick, term_length_ticks)
    `
    )
    .eq('id', id)
    .single()

  if (error) {
    throw error
  }

  return data as PartyWithDetails
}

export async function updatePartyIdeology(
  id: string,
  ideology_vector: IdeologyVector
): Promise<Party> {
  const { data, error } = await supabase
    .from('parties')
    .update({ ideology_vector })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Party
}

export async function fetchMyPartiesWithDetails(): Promise<PartyWithNationWorld[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    return []
  }

  const { data, error } = await supabase
    .from('parties')
    .select(
      `
      *,
      nation:nations!inner(*),
      world:nations!inner(world:worlds!inner(id, name, status))
    `
    )
    .eq('user_id', user.id)
    .eq('is_active', true)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  // Transform the nested data structure
  return (data || []).map((item) => ({
    ...item,
    nation: item.nation,
    world: item.world.world,
  })) as PartyWithNationWorld[]
}

export async function canCreatePartyInNation(nationId: string): Promise<CanCreatePartyResult> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    return { allowed: false, reason: 'Not logged in' }
  }

  // Check if user already has party in nation
  const { data: existingParty } = await supabase
    .from('parties')
    .select('id')
    .eq('nation_id', nationId)
    .eq('user_id', user.id)
    .maybeSingle()

  if (existingParty) {
    return { allowed: false, reason: 'You already have a party in this nation' }
  }

  // Check party count vs max
  const { data: nation } = await supabase
    .from('nations')
    .select('max_parties')
    .eq('id', nationId)
    .single()

  if (!nation) {
    return { allowed: false, reason: 'Nation not found' }
  }

  const { count } = await supabase
    .from('parties')
    .select('*', { count: 'exact', head: true })
    .eq('nation_id', nationId)
    .eq('is_active', true)

  if (count !== null && count >= nation.max_parties) {
    return { allowed: false, reason: 'Nation has reached maximum number of parties' }
  }

  return { allowed: true }
}
